// Branded browser / OS notifications for Animora.
//
// Every notification carries the Animora title + app logo. Chat messages get a "Reply" action
// (with an inline text box on browsers that support it) and friend requests get an "Accept" action.
// Clicks/actions are handled by public/sw.js, which hands them back to the open page.

export const BRAND_NAME = 'Animora'
const ICON = '/icon-192.png'
const BADGE = '/notification-badge.png' // small monochrome mark shown in the Android status bar

export type NotificationPayload = {
  kind: 'message' | 'friend_request' | 'gift' | 'like' | 'comment' | 'call' | 'group_request'
  chatId?: string // Chat.id in the app (friend uid for direct chats, group id for groups)
  chatKind?: 'direct' | 'group'
  fromId?: string
  fromName?: string
  fromAvatar?: string // sender's profile picture (https link) - shown as the notification icon
  msgId?: string // 'message' notifications - lets the ❤️ Like button react to that exact message
  callId?: string // 'call' notifications only - lets the Answer/Decline buttons target the right call
  callType?: 'voice' | 'video'
}

type BrandedAction = { action: string; title: string; type?: 'text'; placeholder?: string }

/** Turns a profile picture value into something a notification can draw: https PNG/JPG/WebP only (no SVG, no data: URL). Presets are mapped to their PNG copy. */
export function notificationIconFor(avatar: unknown): string {
  if (typeof window === 'undefined') return ''
  let a = String(avatar || '').trim()
  if (!a) return ''
  a = a.replace(/^(https?:\/\/[^/]+)?\/avatars\/([A-Za-z0-9_-]+)\.svg$/i, (_m, host, id) => `${host || ''}/avatars/png/${id}.png`)
  if (a.startsWith('/') && !a.startsWith('//')) a = `${window.location.origin}${a}`
  return /^https:\/\/[^\s'"<>]+$/i.test(a) && !/\.svg(\?|$)/i.test(a) && a.length <= 900 ? a : ''
}

export const notificationsSupported = () => typeof window !== 'undefined' && 'Notification' in window

const isNativeApp = () => typeof window !== 'undefined' && !!(window as any).Capacitor?.isNativePlatform?.()

/**
 * Inside the Android app (Capacitor), the web Notification API's permission prompt is not a real
 * OS dialog - it's routed through the WebView and often does not match Android's own system
 * notification-permission dialog (and on Android 13+ it doesn't request the actual POST_NOTIFICATIONS
 * runtime permission at all). The @capacitor/push-notifications plugin talks to Android directly, so
 * we use it there and fall back to the ordinary web flow in a regular browser. Accessed through the
 * window.Capacitor bridge (auto-injected, see the Google Sign-In integration) so no npm import is
 * needed on the web side, and this file still works unmodified when loaded in a plain browser tab.
 * Saves the FCM device token to the person's profile once registered, so a backend can target them later.
 */
async function requestNativePushPermission(onToken?: (token: string) => void): Promise<void> {
  const plugin = (window as any).Capacitor?.Plugins?.PushNotifications
  if (!plugin) return
  try {
    let status = await plugin.checkPermissions()
    if (status.receive !== 'granted') status = await plugin.requestPermissions()
    if (status.receive !== 'granted') return
    if (onToken) {
      plugin.addListener('registration', (token: { value: string }) => onToken(token.value))
      plugin.addListener('registrationError', () => undefined)
    }
    await plugin.register()
  } catch {
    /* ignore - notifications just won't be available this session */
  }
}

/**
 * Asks for notification permission - call it once the person is logged in.
 * Inside the Android app this shows Android's own system permission dialog and registers for FCM.
 * In a browser: Chrome / Edge / Android allow the prompt straight away. Safari and Firefox only allow
 * it after a tap, so if the browser did not show one we ask again on the first tap / key press.
 * Returns a cleanup function.
 */
export function askForNotificationPermission(onFcmToken?: (token: string) => void): () => void {
  if (isNativeApp()) {
    void requestNativePushPermission(onFcmToken)
    return () => undefined
  }
  if (!notificationsSupported() || Notification.permission !== 'default') return () => undefined

  const events = ['click', 'touchend', 'keydown'] as const
  let armed = false
  const disarm = () => {
    if (!armed) return
    armed = false
    events.forEach((name) => window.removeEventListener(name, onGesture))
  }
  const onGesture = () => {
    disarm()
    if (Notification.permission === 'default') void Promise.resolve(Notification.requestPermission()).catch(() => undefined)
  }
  const arm = () => {
    if (armed || Notification.permission !== 'default') return
    armed = true
    events.forEach((name) => window.addEventListener(name, onGesture))
  }

  const startedAt = Date.now()
  try {
    Promise.resolve(Notification.requestPermission())
      .then((result) => {
        // Still "default" almost instantly = no prompt was shown (needs a tap first).
        // If it took longer, the person saw the prompt and dismissed it - don't nag again.
        if ((!result || result === 'default') && Date.now() - startedAt < 1500) arm()
      })
      .catch(arm)
  } catch {
    arm()
  }
  return disarm
}

async function getRegistration(): Promise<ServiceWorkerRegistration | null> {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return null
  try {
    return await Promise.race([
      navigator.serviceWorker.ready,
      new Promise<null>((resolve) => setTimeout(() => resolve(null), 4000)),
    ])
  } catch {
    return null
  }
}

/** Shows an Animora-branded notification (logo + "Animora" title). Silently does nothing without permission. */
export async function showBrandedNotification(opts: {
  body: string
  tag: string
  data: NotificationPayload
  actions?: BrandedAction[]
  renotify?: boolean // replace an earlier notification with the same tag AND alert again
  // Incoming-call notifications use these two: keep the notification on screen until the
  // person taps Answer/Decline (instead of auto-dismissing like a chat ping), and ring with
  // a distinct, more insistent vibration pattern than the default short buzz.
  requireInteraction?: boolean
  vibrate?: number[]
}): Promise<void> {
  if (!notificationsSupported() || Notification.permission !== 'granted') return

  let actions = opts.actions
  const maxActions = (Notification as unknown as { maxActions?: number }).maxActions
  if (actions && typeof maxActions === 'number') actions = actions.slice(0, maxActions)

  // Chat messages: keep every unread message of the chat in ONE notification until the user clears it.
  let shownBody = opts.body
  let shownData: Record<string, unknown> = opts.data as unknown as Record<string, unknown>
  const reg0 = await getRegistration()
  if (reg0 && (opts.data as { kind?: string }).kind === 'message' && opts.tag.startsWith('chat_')) {
    try {
      const prev = await reg0.getNotifications({ tag: opts.tag })
      const pd = (prev[0]?.data || {}) as { lines?: string[]; ids?: string[] }
      let lines: string[] = Array.isArray(pd.lines) ? pd.lines.slice() : (prev[0]?.body ? String(prev[0].body).split('\n') : [])
      let ids: string[] = Array.isArray(pd.ids) ? pd.ids.slice() : []
      const mid = String((opts.data as { msgId?: string }).msgId || '')
      if (!(mid && ids.includes(mid)) && opts.body) { lines.push(opts.body); if (mid) ids.push(mid) }
      lines = lines.slice(-40); ids = ids.slice(-40)
      shownBody = lines.join('\n')
      shownData = { ...(opts.data as unknown as Record<string, unknown>), lines, ids }
    } catch { /* start fresh */ }
  }

  const options: Record<string, unknown> = {
    body: shownBody,
    icon: notificationIconFor(opts.data.fromAvatar) || ICON,
    badge: BADGE,
    tag: opts.tag,
    renotify: Boolean(opts.renotify),
    data: shownData,
    timestamp: Date.now(),
    ...(opts.requireInteraction ? { requireInteraction: true } : {}),
    ...(opts.vibrate ? { vibrate: opts.vibrate } : {}),
    ...(actions && actions.length ? { actions } : {}),
  }

  const registration = await getRegistration()
  if (registration) {
    try {
      await registration.showNotification(BRAND_NAME, options as NotificationOptions)
      return
    } catch { /* fall through to the plain Notification below */ }
  }
  try {
    // Fallback (no service worker): still branded, just without action buttons.
    new Notification(BRAND_NAME, { body: opts.body, icon: ICON, tag: opts.tag, data: opts.data })
  } catch { /* ignore */ }
}

// ---------------------------------------------------------------------------
// Web Push (browser / installed PWA). This is what delivers notifications WITH buttons (Reply, Like, Answer, Decline) while the app
// is fully closed: public/sw.js receives the push and builds the notification. The native Android app does not use this.
// ---------------------------------------------------------------------------
const urlB64ToBytes = (s: string) => {
  const pad = s.replace(/-/g, '+').replace(/_/g, '/')
  const bin = atob(pad + '='.repeat((4 - (pad.length % 4)) % 4))
  return Uint8Array.from(bin, (c) => c.charCodeAt(0))
}

/** Subscribes this browser to Web Push (once permission is granted) and hands the subscription JSON to `save`. Returns a cleanup. */
export function watchWebPush(vapidPublicKey: string, save: (subscriptionJson: string) => void): () => void {
  if (typeof window === 'undefined' || isNativeApp() || !('serviceWorker' in navigator) || !('PushManager' in window) || !notificationsSupported()) return () => undefined
  let stopped = false
  let lastSaved = ''
  const attempt = async () => {
    if (stopped || Notification.permission !== 'granted') return
    try {
      const reg = await getRegistration()
      if (!reg || stopped) return
      let sub = await reg.pushManager.getSubscription()
      if (!sub) sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlB64ToBytes(vapidPublicKey) as unknown as BufferSource })
      const json = JSON.stringify(sub.toJSON())
      if (json !== lastSaved) { lastSaved = json; save(json) }
    } catch { /* push blocked / unsupported: the app still works, just without closed-app pushes */ }
  }
  void attempt()
  // Permission is often granted a moment later (first tap), so keep checking for a while and whenever it changes.
  const timer = setInterval(() => { void attempt() }, 8000)
  const stopTimer = setTimeout(() => clearInterval(timer), 120_000)
  let permStatus: PermissionStatus | null = null
  try {
    void navigator.permissions?.query({ name: 'notifications' as PermissionName }).then((p) => { permStatus = p; p.onchange = () => { void attempt() } }).catch(() => undefined)
  } catch { /* older browsers */ }
  return () => { stopped = true; clearInterval(timer); clearTimeout(stopTimer); if (permStatus) permStatus.onchange = null }
}
